import type { TickerItemOut } from "@hantavirus/shared";
import { createT } from "#/lib/i18n.js";
import type { TemplateLayoutProps } from "#/lib/templates/types.js";
import {
  CommunicationPatternsTable,
  ContradictionsTimeline,
  CountryStatusMap,
  ExposureWindowTracker,
  FAQAccordion,
  Hero,
  InfluencerSpotlight,
  KpiGrid,
  NewsFeed,
  Newswire,
  OfficialSourcesBlock,
  OutbreakGrid,
  OutbreakMap,
  PassengerSilenceCounter,
  PathogenReferenceCard,
  PortEventsMap,
  TickerStrip,
  Timeline,
  UnansweredQuestions,
  VesselLiveTrack,
  toMapRegions,
  toMapShip,
} from "./templateData.js";

export function TemplateNewsroom({
  outbreak,
  outbreaks,
  caseCounts,
  ticker,
  news,
  newsFeedMode,
  timeline,
  faqs,
  regions,
  ships,
  pathogen,
  outbreakSources,
  locale,
}: TemplateLayoutProps) {
  const t = createT(locale, "landing");
  const mapRegions = toMapRegions(regions);
  const mapShip = toMapShip(ships);

  return (
    <main id="main-content" data-template-layout="newsroom">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="space-y-8 sm:space-y-12">
          <Hero outbreak={outbreak} locale={locale} t={t} />
          <KpiGrid
            outbreak={outbreak}
            outbreaks={outbreaks}
            caseCounts={caseCounts}
            locale={locale}
            t={t}
          />

          <PathogenReferenceCard
            pathogen={pathogen}
            locale={locale}
            t={createT(locale, "pathogen")}
          />

          <ExposureWindowTracker slug={outbreak.slug} locale={locale} t={t} />

          <TickerStrip initialItems={ticker as unknown as TickerItemOut[]} locale={locale} t={t} />

          {mapShip && (
            <section id="vessel-live-track" aria-label={createT(locale, "vesselTrack")("title")}>
              <VesselLiveTrack
                mmsi={mapShip.mmsi}
                locale={locale}
                t={createT(locale, "vesselTrack")}
              />
            </section>
          )}

          <section id="map" aria-label={t("section.map")}>
            <OutbreakMap regions={mapRegions} ship={mapShip} locale={locale} t={t} />
          </section>

          <section id="world-status-map" aria-label={createT(locale, "worldMap")("title")}>
            <div className="lg:grid lg:grid-cols-3 lg:gap-8">
              <div className="lg:col-span-2">
                <CountryStatusMap locale={locale} t={createT(locale, "worldMap")} />
              </div>
              <div className="mt-8 lg:mt-0 lg:max-h-[460px]">
                <Newswire locale={locale} t={createT(locale, "worldMap")} />
              </div>
            </div>
          </section>

          {mapShip && (
            <section id="port-events" aria-label={t("section.portEvents")}>
              <PortEventsMap vesselMmsi={mapShip.mmsi} t={createT(locale, "portEvents")} />
            </section>
          )}

          <section id="regions" aria-label={t("section.regions")}>
            <OutbreakGrid slug={outbreak.slug} locale={locale} t={t} initialItems={regions} />
          </section>

          <section id="timeline" aria-label={t("section.timeline")}>
            <Timeline slug={outbreak.slug} locale={locale} t={t} initialItems={timeline} />
          </section>

          <div className="lg:grid lg:grid-cols-3 lg:gap-8">
            <div className="lg:col-span-2">
              <PassengerSilenceCounter
                slug={outbreak.slug}
                locale={locale}
                t={createT(locale, "passengerSilence")}
              />
            </div>
            <div className="mt-8 lg:mt-0">
              <InfluencerSpotlight
                slug={outbreak.slug}
                locale={locale}
                t={createT(locale, "passengerSilence")}
              />
            </div>
          </div>

          <CommunicationPatternsTable locale={locale} t={t} />
          <ContradictionsTimeline locale={locale} t={createT(locale, "contradictions")} />
          <UnansweredQuestions locale={locale} t={createT(locale, "contradictions")} />

          <div className="lg:grid lg:grid-cols-3 lg:gap-8">
            <section id="news" aria-label={t("section.news")} className="lg:col-span-2">
              <NewsFeed
                slug={outbreak.slug}
                locale={locale}
                t={t}
                initialItems={news}
                feedMode={newsFeedMode}
              />
            </section>
            <section
              id="faq"
              aria-label={t("section.faq")}
              className="mt-8 lg:mt-0 lg:sticky lg:top-8 lg:self-start"
            >
              <FAQAccordion slug={outbreak.slug} locale={locale} t={t} initialItems={faqs} />
            </section>
          </div>

          <OfficialSourcesBlock
            sources={outbreakSources ?? []}
            locale={locale}
            t={createT(locale, "pathogen")}
          />
        </div>
      </div>
    </main>
  );
}
